/**
 * Where a gateway's sender IDs come from, as a real choice (user,
 * 18/10/2026: "clicking Saved IDs I don't really understand" — it looked
 * like a tab but switched the mode): your own IDs, the same on every drop,
 * or names the AI writes at each generation — and, under it, what the
 * choice does with what is set right now (no ID = no sender).
 * For Media Devoted | Created By Ciro Autiero
 */
import { FC } from 'react';

import { HintDot } from '@/components/ui';
import { cn } from '@/lib/utils';

import { ChoiceChips } from '../../../general/controls/ChoiceChips';

type Source = 'saved' | 'ai';

const OPTIONS = [
  { value: 'saved', label: 'My sender IDs' },
  { value: 'ai', label: 'AI writes them' },
] as const;

const HINT =
  'The sender ID is the name the SMS shows as its sender (up to 11 letters or digits). My sender IDs: the IDs you save here go on every drop from this gateway. AI writes them: at each generation the AI writes new names from the prompt group below; IDs you saved still go out with them.';

interface Props {
  automatic: boolean;
  ids: string[];
  amount: number;
  mode: 'SHARED' | 'UNIQUE';
  /** Prompt groups the AI writes from — 0 = it writes none. */
  groups: number;
  onChange: (automatic: boolean) => void;
}

const plural = (n: number, word: string) => `${n} ${word}${n === 1 ? '' : 's'}`;

/** The engine's rule (sender-units): SHARED = one set per prompt group, the groups rotating across drops. */
function aiWords(p: Pick<Props, 'amount' | 'mode' | 'groups'>): string {
  const names = plural(p.amount, 'new name');
  if (p.mode === 'UNIQUE') return `${names} for each drop${p.groups > 1 ? `, from its group (${p.groups} groups take turns)` : ''}`;
  if (p.groups > 1) return `${names} per group at each generation (${p.groups} groups take turns across the drops; each set is shared by its group's drops)`;
  return `${names} at each generation, shared by every drop on this gateway`;
}

export function sourceLine(p: Omit<Props, 'onChange'>): { text: string; warn: boolean } {
  if (!p.automatic) {
    if (p.ids.length === 0) return { text: "No ID yet — add one below, or this gateway's drops go out without a sender.", warn: true };
    return { text: `${p.ids.join(', ')} — the same on every drop from this gateway.`, warn: false };
  }
  if (p.groups === 0)
    return { text: `Pick a prompt group below — without one the AI writes no names${p.ids.length > 0 ? ' (your saved IDs still go out)' : ", and this gateway's drops go out without a sender"}.`, warn: true };
  const saved = p.ids.length > 0 ? ` · your ${plural(p.ids.length, 'saved ID')} ${p.ids.length === 1 ? 'goes' : 'go'} out with them` : '';
  return { text: `The AI writes ${aiWords(p)}${saved}.`, warn: false };
}

export const SenderSourceChoice: FC<Props> = ({ onChange, ...facts }) => {
  const line = sourceLine(facts);
  return (
    <div className="flex flex-col gap-2" data-testid="sender-source">
      <div className="flex flex-wrap items-center gap-3">
        <span className="flex items-center gap-1 text-[10.5px] font-semibold uppercase tracking-wider text-muted-foreground">
          Sender IDs <HintDot label={HINT} />
        </span>
        <ChoiceChips<Source> options={OPTIONS} value={facts.automatic ? 'ai' : 'saved'} onChange={(v) => onChange(v === 'ai')} ariaLabel="Where this gateway's sender IDs come from" />
      </div>
      <p key={`${facts.automatic}-${line.warn}`} className={cn('text-xs duration-300 animate-in fade-in-0 motion-reduce:animate-none', line.warn ? 'font-medium text-warning' : 'text-muted-foreground')}>
        {line.text}
      </p>
    </div>
  );
};
